import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiX, FiStar, FiArrowRight } from 'react-icons/fi';

const Comparison: React.FC = () => {
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

  const offers = [
    {
      id: 'circusbet',
      name: 'CircusBet',
      bonus: '100€ + 50 tours',
      rating: 4.8,
      image: '/images/offers/circusbet.webp',
      link: '/offres/circusbet',
      url: 'https://www.circusbet.fr/',
      minDeposit: '10€',
      withdrawal: '24-48h',
      license: 'ANJ',
      features: {
        'Pari en direct': true,
        'Cash-out': true,
        'Streaming': true,
        'Application mobile': true,
        'Support 24/7': true,
        'Programme VIP': false,
        'Cashback': false,
      },
    },
    {
      id: 'genybet',
      name: 'GenyBet',
      bonus: '150€',
      rating: 4.7,
      image: '/images/offers/genybet.webp',
      link: '/offres/genybet',
      url: 'https://www.genybet.fr/',
      minDeposit: '20€',
      withdrawal: '12-24h',
      license: 'ANJ',
      features: {
        'Pari en direct': true,
        'Cash-out': true,
        'Streaming': true,
        'Application mobile': true,
        'Support 24/7': true,
        'Programme VIP': true,
        'Cashback': false,
      },
    },
    {
      id: 'netbet',
      name: 'NetBet',
      bonus: '200€ + 100 tours',
      rating: 4.9,
      image: '/images/offers/netbet.webp',
      link: '/offres/netbet',
      url: 'https://www.netbet.fr/',
      minDeposit: '15€',
      withdrawal: 'Instant',
      license: 'ANJ',
      features: {
        'Pari en direct': true,
        'Cash-out': true,
        'Streaming': true,
        'Application mobile': true,
        'Support 24/7': true,
        'Programme VIP': true,
        'Cashback': true,
      },
    },
  ];

  const allFeatures = Array.from(
    new Set(offers.flatMap((o) => Object.keys(o.features)))
  );

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Table',
            about: 'Comparaison des bookmakers de paris sportifs en France',
          }),
        }}
      />

      <div className="min-h-screen pt-20">
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl font-bold text-white text-center mb-4">
              Comparaison des Bookmakers
            </h1>
            <p className="text-center text-dark-muted text-xl mb-12 max-w-3xl mx-auto">
              Comparez les meilleurs bookmakers français en un coup d'œil. Trouvez celui qui
              correspond le mieux à vos besoins.
            </p>

            {/* Mobile Cards View */}
            <div className="md:hidden space-y-6">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-dark-card border border-dark-border rounded-lg overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={offer.image}
                        alt={offer.name}
                        className="h-16 w-auto"
                        loading="lazy"
                      />
                      <div className="flex-1">
                        <div className="text-white font-bold text-xl mb-1">{offer.name}</div>
                        <div className="flex items-center mb-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <FiStar
                              key={i}
                              className={
                                i < Math.floor(offer.rating)
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-dark-muted'
                              }
                              size={16}
                            />
                          ))}
                          <span className="ml-2 text-white text-sm">{offer.rating}</span>
                        </div>
                        <div className="text-accent-primary font-semibold text-sm">
                          {offer.bonus}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center py-2 border-b border-dark-border">
                        <span className="text-dark-muted">Dépôt minimum</span>
                        <span className="text-white font-semibold">{offer.minDeposit}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-dark-border">
                        <span className="text-dark-muted">Temps de retrait</span>
                        <span className="text-white font-semibold">{offer.withdrawal}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-dark-border">
                        <span className="text-dark-muted">Licence</span>
                        <span className="text-white font-semibold">{offer.license}</span>
                      </div>
                      {allFeatures.map((feature) => (
                        <div
                          key={feature}
                          className="flex justify-between items-center py-2 border-b border-dark-border"
                        >
                          <span className="text-dark-muted">{feature}</span>
                          {offer.features[feature as keyof typeof offer.features] ? (
                            <FiCheck className="text-accent-success" size={20} />
                          ) : (
                            <FiX className="text-red-400" size={20} />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex flex-col gap-2 mt-4">
                      <a
                        href={offer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-accent-primary hover:bg-accent-primary/90 text-white text-sm font-semibold py-3 px-4 rounded-lg text-center transition-colors"
                      >
                        Jouer maintenant
                      </a>
                      <Link
                        to={offer.link}
                        className="text-accent-primary hover:text-accent-secondary text-sm font-semibold text-center py-2"
                      >
                        Voir l'avis →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <div className="bg-dark-card border border-dark-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-dark-surface">
                    <tr>
                      <th className="px-4 lg:px-6 py-4 text-left text-white font-semibold text-sm lg:text-base">
                        Critères
                      </th>
                      {offers.map((offer) => (
                        <th
                          key={offer.id}
                          className="px-4 lg:px-6 py-4 text-center min-w-[180px] lg:min-w-[200px]"
                        >
                          <div className="flex flex-col items-center">
                            <img
                              src={offer.image}
                              alt={offer.name}
                              className="h-12 lg:h-16 w-auto mb-2 lg:mb-3"
                              loading="lazy"
                            />
                            <div className="text-white font-bold text-base lg:text-lg mb-1">
                              {offer.name}
                            </div>
                            <div className="flex items-center justify-center mb-2">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <FiStar
                                  key={i}
                                  className={
                                    i < Math.floor(offer.rating)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-dark-muted'
                                  }
                                  size={14}
                                />
                              ))}
                              <span className="ml-1 lg:ml-2 text-white text-xs lg:text-sm">
                                {offer.rating}
                              </span>
                            </div>
                            <div className="text-accent-primary font-semibold text-xs lg:text-sm mb-2 lg:mb-3">
                              {offer.bonus}
                            </div>
                            <div className="flex flex-col gap-2 w-full">
                              <a
                                href={offer.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-accent-primary hover:bg-accent-primary/90 text-white text-xs lg:text-sm font-semibold py-1.5 lg:py-2 px-2 lg:px-4 rounded-lg text-center transition-colors"
                              >
                                Jouer maintenant
                              </a>
                              <Link
                                to={offer.link}
                                className="text-accent-primary hover:text-accent-secondary text-xs lg:text-sm font-semibold text-center"
                              >
                                Voir l'avis →
                              </Link>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-dark-border">
                      <td className="px-4 lg:px-6 py-3 lg:py-4 text-white font-semibold text-sm lg:text-base">
                        Dépôt minimum
                      </td>
                      {offers.map((offer) => (
                        <td
                          key={offer.id}
                          className="px-4 lg:px-6 py-3 lg:py-4 text-center text-dark-muted text-sm lg:text-base"
                        >
                          {offer.minDeposit}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t border-dark-border bg-dark-surface/50">
                      <td className="px-4 lg:px-6 py-3 lg:py-4 text-white font-semibold text-sm lg:text-base">
                        Temps de retrait
                      </td>
                      {offers.map((offer) => (
                        <td
                          key={offer.id}
                          className="px-4 lg:px-6 py-3 lg:py-4 text-center text-dark-muted text-sm lg:text-base"
                        >
                          {offer.withdrawal}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t border-dark-border">
                      <td className="px-4 lg:px-6 py-3 lg:py-4 text-white font-semibold text-sm lg:text-base">
                        Licence
                      </td>
                      {offers.map((offer) => (
                        <td
                          key={offer.id}
                          className="px-4 lg:px-6 py-3 lg:py-4 text-center text-dark-muted text-sm lg:text-base"
                        >
                          {offer.license}
                        </td>
                      ))}
                    </tr>
                    {allFeatures.map((feature) => (
                      <tr
                        key={feature}
                        className={`border-t border-dark-border ${
                          selectedFeature === feature ? 'bg-accent-primary/10' : ''
                        }`}
                        onMouseEnter={() => setSelectedFeature(feature)}
                        onMouseLeave={() => setSelectedFeature(null)}
                      >
                        <td className="px-4 lg:px-6 py-3 lg:py-4 text-white font-semibold text-sm lg:text-base">
                          {feature}
                        </td>
                        {offers.map((offer) => (
                          <td
                            key={offer.id}
                            className="px-4 lg:px-6 py-3 lg:py-4 text-center"
                          >
                            {offer.features[feature as keyof typeof offer.features] ? (
                              <FiCheck className="mx-auto text-accent-success" size={20} />
                            ) : (
                              <FiX className="mx-auto text-red-400" size={20} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recommendations */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Pour les débutants',
                  offer: offers[0],
                  reason: 'Interface simple et bonus généreux',
                },
                {
                  title: 'Pour les parieurs réguliers',
                  offer: offers[1],
                  reason: 'Programme VIP et fonctionnalités avancées',
                },
                {
                  title: 'Pour les experts',
                  offer: offers[2],
                  reason: 'Meilleures cotes et cashback',
                },
              ].map((rec, i) => (
                <div
                  key={i}
                  className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-accent-primary transition-all"
                >
                  <h3 className="text-xl font-bold text-white mb-2">{rec.title}</h3>
                  <div className="text-accent-primary font-semibold mb-2">
                    {rec.offer.name}
                  </div>
                  <p className="text-dark-muted text-sm mb-4">{rec.reason}</p>
                  <div className="flex flex-col gap-2">
                    <a
                      href={rec.offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold text-sm py-2 px-4 rounded-lg text-center transition-colors"
                    >
                      Jouer maintenant
                    </a>
                    <Link
                      to={rec.offer.link}
                      className="text-accent-primary hover:text-accent-secondary font-semibold text-sm text-center inline-flex items-center justify-center"
                    >
                      En savoir plus <FiArrowRight className="ml-2" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Comparison;

